import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { AREAS } from "../data/site";

export const Areas = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="aree" className="py-20 lg:py-32 bg-[#1C211E] relative grain-overlay" data-testid="areas-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="02" label="Aree di intervento" dark />
        <Reveal delay={0.08}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] mt-5 text-[#F5F3ED] max-w-2xl">
            In cosa posso <em className="text-[#D4A359] not-italic">aiutarti</em>
          </h2>
          <p className="mt-6 text-base text-[#F5F3ED]/60 leading-relaxed max-w-2xl">
            Ogni percorso nasce dall'ascolto della persona e della sua storia. Lavoro principalmente con giovani adulti,
            offrendo percorsi psicologici e psicoterapeutici costruiti sulle esigenze e sugli obiettivi individuali.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AREAS.map((area, i) => (
            <Reveal key={area.n} delay={0.06 * i}>
              <button onClick={() => setSelected(area)} data-testid={`area-card-${area.n}`}
                      className="group w-full text-left h-full bg-[#F5F3ED]/[0.04] hover:bg-[#F5F3ED]/[0.08] border border-[#F5F3ED]/10 hover:border-[#D4A359]/40 rounded-2xl p-8 transition-colors duration-500">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs tracking-[0.25em] text-[#D4A359]">{area.n}</span>
                  <ArrowUpRight size={18} className="text-[#F5F3ED]/40 transition-all duration-300 group-hover:text-[#D4A359] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium leading-snug text-[#F5F3ED] mt-6">{area.title}</h3>
                <p className="mt-3 text-sm text-[#F5F3ED]/60 leading-relaxed">{area.short}</p>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <div className="border-l-2 border-[#D4A359] pl-6 sm:pl-8 max-w-3xl" data-testid="areas-closing-note">
            <h3 className="font-serif text-2xl sm:text-3xl font-medium text-[#D4A359]">Ogni percorso parte dalla persona</h3>
            <p className="mt-4 text-base text-[#F5F3ED]/70 leading-relaxed">
              Queste aree servono a orientarsi, ma nessuna persona coincide con una categoria o con un sintomo.
              Durante i primi colloqui cercheremo di comprendere insieme cosa ti sta portando a chiedere aiuto,
              cosa sta accadendo nella tua vita e quale possa essere il percorso più adatto a te.
            </p>
            <p className="mt-4 font-serif italic text-lg text-[#F5F3ED]/85 leading-relaxed">
              «Non devi arrivare sapendo già esattamente quale sia il problema. Possiamo partire proprio da ciò
              che oggi senti di non riuscire ancora a comprendere.»
            </p>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[60] bg-[#1C1E1D]/70 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setSelected(null)} data-testid="area-modal-overlay">
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative bg-[#FBF9F5] rounded-2xl max-w-xl w-full p-8 sm:p-10 shadow-2xl"
                        onClick={(e) => e.stopPropagation()} data-testid="area-modal">
              <button onClick={() => setSelected(null)} aria-label="Chiudi" data-testid="area-modal-close"
                      className="absolute top-5 right-5 p-2 rounded-full hover:bg-[#F3EFE6] transition-colors">
                <X size={20} />
              </button>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#C85A32]">Area {selected.n}</p>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium mt-4 text-[#1C1E1D]">{selected.title}</h3>
              <div className="mt-5 space-y-4">
                {selected.long.map((p, i) => (
                  <p key={i} className="text-base text-[#525754] leading-relaxed">{p}</p>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
